import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Users, IndianRupee, ShoppingBag, Search, Shield, CheckCircle, Edit, MessageSquare, Stethoscope, UserPlus } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyCustomer = {
  name: '',
  phone: '',
  email: '',
  address: '',
  abha_id: '',
  referring_doctor_id: ''
};

const emptyDoctor = {
  name: '',
  specialization: '',
  phone: '',
  address: ''
};

function Customers() {
  const [customers, setCustomers] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddDoctorModal, setShowAddDoctorModal] = useState(false);
  const [formData, setFormData] = useState(emptyCustomer);
  const [doctorForm, setDoctorForm] = useState(emptyDoctor);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [whatsappEnabled, setWhatsappEnabled] = useState(false);

  const fetchCustomers = useCallback(async function() {
    try {
      const params = searchTerm ? `?search=${searchTerm}` : '';
      const response = await axios.get(`${API}/customers${params}`);
      setCustomers(response.data);
    } catch (error) {
      toast.error('Failed to fetch customers');
    }
  }, [searchTerm]);

  const fetchDoctors = useCallback(async function() {
    try {
      const response = await axios.get(`${API}/doctors`);
      setDoctors(response.data);
    } catch (error) {
      console.log('Failed to fetch doctors');
    }
  }, []);

  useEffect(function() {
    fetchCustomers();
    fetchDoctors();
    checkWhatsappStatus();
  }, [fetchCustomers, fetchDoctors]);

  async function checkWhatsappStatus() {
    try {
      const res = await axios.get(`${API}/whatsapp-settings`);
      setWhatsappEnabled(res.data.status === 'connected');
    } catch (e) {
      setWhatsappEnabled(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/customers`, formData);
      toast.success('Customer added');
      setShowAddModal(false);
      setFormData(emptyCustomer);
      fetchCustomers();
    } catch (error) {
      const msg = error.response && error.response.data && error.response.data.detail;
      toast.error(msg || 'Failed to add customer');
    } finally {
      setLoading(false);
    }
  }

  async function handleUpdate(e) {
    e.preventDefault();
    if (!editingCustomer) return;
    
    setLoading(true);
    try {
      await axios.put(`${API}/customers/${editingCustomer.id}`, formData);
      toast.success('Customer updated');
      setShowEditModal(false);
      setEditingCustomer(null);
      setFormData(emptyCustomer);
      fetchCustomers();
    } catch (error) {
      const msg = error.response && error.response.data && error.response.data.detail;
      toast.error(msg || 'Failed to update customer');
    } finally {
      setLoading(false);
    }
  }

  async function handleAddDoctor(e) {
    e.preventDefault();
    if (!doctorForm.name.trim()) {
      toast.error('Doctor name is required');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/doctors`, doctorForm);
      toast.success('Doctor added');
      setShowAddDoctorModal(false);
      setDoctorForm(emptyDoctor);
      fetchDoctors();
      // Auto-select the new doctor
      setFormData({ ...formData, referring_doctor_id: res.data.id });
    } catch (error) {
      toast.error('Failed to add doctor');
    } finally {
      setLoading(false);
    }
  }

  async function verifyAbha(customerId) {
    try {
      const res = await axios.post(`${API}/customers/${customerId}/verify-abha`);
      if (res.data.status === 'verified') {
        toast.success('ABHA ID verified successfully');
        fetchCustomers();
      } else {
        toast.error(res.data.message);
      }
    } catch (error) {
      const msg = error.response && error.response.data && error.response.data.detail;
      toast.error(msg || 'Failed to verify ABHA ID');
    }
  }

  function openEditModal(customer) {
    setEditingCustomer(customer);
    setFormData({
      name: customer.name,
      phone: customer.phone || '',
      email: customer.email || '',
      address: customer.address || '',
      abha_id: customer.abha_id || '',
      referring_doctor_id: customer.referring_doctor_id || ''
    });
    setShowEditModal(true);
  }

  function handleModalClose(open) {
    setShowAddModal(open);
    if (!open) {
      setFormData(emptyCustomer);
    }
  }

  function handleEditModalClose(open) {
    setShowEditModal(open);
    if (!open) {
      setEditingCustomer(null);
      setFormData(emptyCustomer);
    }
  }

  function updateFormField(field, value) {
    setFormData({ ...formData, [field]: value });
  }

  function updateDoctorField(field, value) {
    setDoctorForm({ ...doctorForm, [field]: value });
  }

  function calculateTotalPurchases() {
    let total = 0;
    for (let i = 0; i < customers.length; i++) {
      total += customers[i].total_purchases;
    }
    return total;
  }

  function calculateTotalVisits() {
    let total = 0;
    for (let i = 0; i < customers.length; i++) {
      total += customers[i].visit_count;
    }
    return total;
  }

  const totalPurchases = calculateTotalPurchases();
  const totalVisits = calculateTotalVisits();

  function renderDoctorOptions() {
    const options = [];
    options.push(<SelectItem key="none" value="none">No Referring Doctor</SelectItem>);
    for (let i = 0; i < doctors.length; i++) {
      const doc = doctors[i];
      options.push(
        <SelectItem key={doc.id} value={doc.id}>
          {doc.name} {doc.specialization ? `(${doc.specialization})` : ''}
        </SelectItem>
      );
    }
    return options;
  }

  function renderFormFields() {
    return (
      <>
        <div className="space-y-2">
          <Label htmlFor="name">Name *</Label>
          <Input
            id="name"
            data-testid="customer-name"
            value={formData.name}
            onChange={function(e) { updateFormField('name', e.target.value); }}
            placeholder="Customer name"
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="phone">Phone</Label>
          <Input
            id="phone"
            data-testid="customer-phone"
            value={formData.phone}
            onChange={function(e) { updateFormField('phone', e.target.value); }}
            placeholder="9876543210"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            data-testid="customer-email"
            value={formData.email}
            onChange={function(e) { updateFormField('email', e.target.value); }}
            placeholder="customer@email.com"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="address">Address</Label>
          <Input
            id="address"
            data-testid="customer-address"
            value={formData.address}
            onChange={function(e) { updateFormField('address', e.target.value); }}
            placeholder="Customer address"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="abha_id" className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-blue-600" />
            ABHA ID (Health ID)
          </Label>
          <Input
            id="abha_id"
            data-testid="customer-abha-id"
            value={formData.abha_id}
            onChange={function(e) { updateFormField('abha_id', e.target.value); }}
            placeholder="XX-XXXX-XXXX-XXXX"
          />
          <p className="text-xs text-slate-500">14-digit ABDM Health ID for integrated health records</p>
        </div>
        
        {/* Referring Doctor Selection */}
        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            <Stethoscope className="w-4 h-4 text-purple-600" />
            Referring Doctor
          </Label>
          <div className="flex gap-2">
            <Select 
              value={formData.referring_doctor_id || 'none'} 
              onValueChange={function(v) { updateFormField('referring_doctor_id', v === 'none' ? '' : v); }}
            >
              <SelectTrigger className="flex-1" data-testid="referring-doctor-select">
                <SelectValue placeholder="Select doctor" />
              </SelectTrigger>
              <SelectContent>
                {renderDoctorOptions()}
              </SelectContent>
            </Select>
            <Button 
              type="button" 
              variant="outline" 
              size="icon"
              onClick={function() { setShowAddDoctorModal(true); }}
              title="Add New Doctor"
            >
              <UserPlus className="w-4 h-4" />
            </Button>
          </div>
          <p className="text-xs text-slate-500">Select the doctor who referred this customer, or add a new one</p>
        </div>
      </>
    );
  }

  function renderCustomerCards() {
    if (customers.length === 0) {
      return (
        <div className="col-span-full text-center py-12 text-slate-400">
          <Users className="w-16 h-16 mx-auto mb-4 opacity-50" />
          <p className="text-lg">No customers registered yet</p>
          <p className="text-sm">Add customers to track their purchases</p>
        </div>
      );
    }

    const cards = [];
    for (let i = 0; i < customers.length; i++) {
      const customer = customers[i];
      const initial = customer.name.charAt(0).toUpperCase();
      cards.push(
        <Card key={customer.id} className="doctor-card" data-testid={`customer-card-${customer.id}`}>
          <CardContent className="p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-navy-100 rounded-full flex items-center justify-center text-navy-700 font-bold text-lg">
                  {initial}
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-navy-900">{customer.name}</h3>
                  <p className="text-sm text-slate-500">{customer.phone || 'No phone'}</p>
                </div>
              </div>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={function() { openEditModal(customer); }}>
                <Edit className="w-4 h-4 text-slate-500" />
              </Button>
            </div>

            {customer.email && (
              <p className="text-sm text-slate-600 mb-2">{customer.email}</p>
            )}
            
            {/* Referring Doctor */}
            {customer.referring_doctor_name && (
              <div className="mb-2 p-2 bg-purple-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Stethoscope className="w-4 h-4 text-purple-600" />
                  <span className="text-xs text-purple-700">Referred by: <strong>{customer.referring_doctor_name}</strong></span>
                </div>
              </div>
            )}
            
            {/* ABHA ID Section */}
            <div className="mb-3 p-2 bg-slate-50 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-blue-600" />
                  <span className="text-xs text-slate-600">ABHA ID</span>
                </div>
                {customer.abha_id ? (
                  customer.abha_verified ? (
                    <Badge className="bg-green-100 text-green-800 text-xs">
                      <CheckCircle className="w-3 h-3 mr-1" />Verified
                    </Badge>
                  ) : (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="h-6 text-xs"
                      onClick={function() { verifyAbha(customer.id); }}
                    >
                      Verify
                    </Button>
                  )
                ) : (
                  <Badge className="bg-slate-100 text-slate-500 text-xs">Not Added</Badge>
                )}
              </div>
              {customer.abha_id && (
                <p className="text-sm font-mono mt-1">{customer.abha_id}</p>
              )}
            </div>

            <div className="border-t pt-3 mt-3">
              <div className="grid grid-cols-2 gap-2 text-center">
                <div>
                  <p className="text-lg font-bold text-mint-600">₹{customer.total_purchases.toFixed(0)}</p>
                  <p className="text-xs text-slate-500">Total Purchases</p>
                </div>
                <div>
                  <p className="text-lg font-bold text-navy-600">{customer.visit_count}</p>
                  <p className="text-xs text-slate-500">Visits</p>
                </div>
              </div>
            </div>
            
            {whatsappEnabled && customer.phone && (
              <div className="mt-3 pt-3 border-t">
                <Button variant="outline" size="sm" className="w-full text-green-600 border-green-200 hover:bg-green-50">
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Send WhatsApp
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      );
    }
    return cards;
  }

  return (
    <div className="p-4 lg:p-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-heading font-bold text-navy-900">Customer Management</h1>
          <p className="text-slate-500 text-sm">Track customer purchases, visits, and referrals</p>
        </div>
        <Dialog open={showAddModal} onOpenChange={handleModalClose}>
          <DialogTrigger asChild>
            <Button className="bg-mint-600 hover:bg-mint-700" data-testid="add-customer-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Customer
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add New Customer</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {renderFormFields()}
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={function() { setShowAddModal(false); }}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-mint-600 hover:bg-mint-700" disabled={loading}>
                  {loading ? 'Adding...' : 'Add Customer'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={handleEditModalClose}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Customer</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdate} className="space-y-4">
            {renderFormFields()}
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={function() { setShowEditModal(false); }}>
                Cancel
              </Button>
              <Button type="submit" className="bg-mint-600 hover:bg-mint-700" disabled={loading}>
                {loading ? 'Updating...' : 'Update Customer'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Doctor Modal */}
      <Dialog open={showAddDoctorModal} onOpenChange={setShowAddDoctorModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Stethoscope className="w-5 h-5 text-purple-600" />
              Add New Doctor
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddDoctor} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="doc_name">Doctor Name *</Label>
              <Input
                id="doc_name"
                value={doctorForm.name}
                onChange={function(e) { updateDoctorField('name', e.target.value); }}
                placeholder="Dr. Name"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="doc_spec">Specialization</Label>
              <Input
                id="doc_spec"
                value={doctorForm.specialization}
                onChange={function(e) { updateDoctorField('specialization', e.target.value); }}
                placeholder="General Physician, Cardiologist, etc."
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="doc_phone">Phone</Label>
              <Input
                id="doc_phone"
                value={doctorForm.phone}
                onChange={function(e) { updateDoctorField('phone', e.target.value); }}
                placeholder="Phone number"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="doc_address">Clinic Address</Label>
              <Input
                id="doc_address"
                value={doctorForm.address}
                onChange={function(e) { updateDoctorField('address', e.target.value); }}
                placeholder="Clinic/Hospital address"
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={function() { setShowAddDoctorModal(false); setDoctorForm(emptyDoctor); }}>
                Cancel
              </Button>
              <Button type="submit" className="bg-purple-600 hover:bg-purple-700" disabled={loading}>
                {loading ? 'Adding...' : 'Add Doctor'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-navy-100 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-navy-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Customers</p>
                <p className="text-xl font-bold text-navy-900">{customers.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-mint-100 rounded-lg flex items-center justify-center">
                <IndianRupee className="w-5 h-5 text-mint-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Revenue</p>
                <p className="text-xl font-bold text-navy-900">₹{totalPurchases.toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <ShoppingBag className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Visits</p>
                <p className="text-xl font-bold text-navy-900">{totalVisits}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Stethoscope className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">With Referrals</p>
                <p className="text-xl font-bold text-navy-900">
                  {customers.filter(function(c) { return c.referring_doctor_id; }).length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="mb-4 relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          data-testid="customer-search"
          placeholder="Search by name, phone, or ABHA ID..."
          value={searchTerm}
          onChange={function(e) { setSearchTerm(e.target.value); }}
          className="pl-10"
        />
      </div>

      {/* Customers Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {renderCustomerCards()}
      </div>
    </div>
  );
}

export default Customers;
